import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Send, CheckCheck, MailOpen, MousePointerClick, Reply, AlertTriangle } from "lucide-react";

export function PageHeader({ eyebrow, title, subtitle, actions }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow && (
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.25em] text-brand">
            {eyebrow}
          </p>
        )}
        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">{title}</h1>
        {subtitle && <p className="mt-2 max-w-2xl text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ icon: Icon, label, value, hint, delay = 0, accent = "yellow", to, onClick, testid }) {
  const accentCls = accent === "red" ? "text-brema-red dark:text-brand" : accent === "grey" ? "text-muted-foreground" : "text-brand";
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        {Icon && <Icon className={`h-[18px] w-[18px] ${accentCls}`} />}
      </div>
      <p className="mt-3 text-3xl font-bold tracking-tight text-foreground">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </>
  );
  const clickableCls = "group block w-full cursor-pointer rounded-xl border border-border bg-brema-card p-5 text-left transition-colors duration-200 hover:border-primary/50";
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay }}
    >
      {to ? (
        <Link to={to} data-testid={testid} className={clickableCls}>
          {inner}
        </Link>
      ) : onClick ? (
        <button type="button" onClick={onClick} data-testid={testid} className={clickableCls}>
          {inner}
        </button>
      ) : (
        <div data-testid={testid} className="rounded-xl border border-border bg-brema-card p-5 transition-colors duration-200 hover:border-border">
          {inner}
        </div>
      )}
    </motion.div>
  );
}

export function ScoreBadge({ score }) {
  const s = Number(score) || 0;
  const color =
    s >= 80 ? "bg-primary text-primary-foreground" :
    s >= 60 ? "bg-brema-navy text-white dark:bg-emerald-500/20 dark:text-emerald-300 dark:border dark:border-emerald-500/30" :
    "bg-muted text-muted-foreground border border-border";
  return (
    <span className={`inline-flex h-6 min-w-[80px] shrink-0 items-center justify-center rounded-full px-2.5 text-xs font-semibold tabular-nums ${color}`}>
      Match {s}
    </span>
  );
}

const STATUS_STYLES = {
  nuovo: "bg-muted text-muted-foreground border border-border",
  contattato: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30",
  "in trattativa": "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30",
  attivo: "bg-positive/15 text-positive border border-positive/40",
  qualificato: "bg-primary/15 text-primary dark:text-brand border border-primary/30",
  interessato: "bg-positive/15 text-positive border border-positive/40",
  scartato: "bg-brema-red/15 text-red-700 dark:text-red-300 border border-brema-red/40",
  "non contattare": "bg-red-600/20 text-red-700 dark:text-red-300 border border-red-600/50 font-semibold",
  annullata: "bg-red-600/15 text-red-700 dark:text-red-300 border border-red-600/40",
  ricevuto: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30",
  bozza: "bg-muted text-muted-foreground border border-border",
  inviata: "bg-positive/15 text-positive border border-positive/40",
};

export function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.nuovo;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${cls}`}>
      {status}
    </span>
  );
}


const ORDER_STATUS = {
  in_elaborazione: { label: "In elaborazione", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30" },
  spedito: { label: "Spedito", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/30" },
  completato: { label: "Completato", cls: "bg-positive/15 text-positive border border-positive/40" },
  ricevuto: { label: "In elaborazione", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/30" },
};

export const ORDER_STATUS_OPTIONS = [
  { value: "in_elaborazione", label: "In elaborazione" },
  { value: "spedito", label: "Spedito" },
  { value: "completato", label: "Completato" },
];

export function OrderStatusBadge({ status }) {
  const s = ORDER_STATUS[status] || ORDER_STATUS.in_elaborazione;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}


export function EmailFunnel({ history }) {
  const h = history || [];
  const sent = h.length;
  const delivered = h.filter((m) => ["consegnata", "aperta", "click", "risposta"].includes(m.delivery_status)).length;
  const opened = h.filter((m) => ["aperta", "click", "risposta"].includes(m.delivery_status)).length;
  const clicked = h.filter((m) => m.delivery_status === "click").length;
  const replied = h.filter((m) => m.delivery_status === "risposta").length;
  const bounced = h.filter((m) => ["rimbalzata", "reclamo"].includes(m.delivery_status)).length;
  const pct = (n) => (sent ? Math.round((n / sent) * 100) : 0);
  const stats = [
    { key: "inviate", label: "Inviate", value: sent, sub: null, icon: Send, cls: "text-foreground" },
    { key: "consegnate", label: "Consegnate", value: delivered, sub: `${pct(delivered)}%`, icon: CheckCheck, cls: "text-sky-600 dark:text-sky-300" },
    { key: "aperte", label: "Aperte", value: opened, sub: `${pct(opened)}%`, icon: MailOpen, cls: "text-positive" },
    { key: "cliccate", label: "Cliccate", value: clicked, sub: `${pct(clicked)}%`, icon: MousePointerClick, cls: "text-brand" },
    { key: "risposte", label: "Risposte", value: replied, sub: `${pct(replied)}%`, icon: Reply, cls: "text-emerald-600 dark:text-emerald-400" },
    { key: "rimbalzate", label: "Rimbalzate", value: bounced, sub: `${pct(bounced)}%`, icon: AlertTriangle, cls: "text-red-600 dark:text-red-300" },
  ];
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="email-funnel">
      {stats.map((s) => {
        const Icon = s.icon;
        return (
          <div key={s.key} data-testid={`funnel-${s.key}`} className="rounded-xl border border-border bg-brema-card p-3">
            <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="h-3 w-3" /> {s.label}</p>
            <p className={`mt-1 text-xl font-bold ${s.cls}`}>{s.value}{s.sub && <span className="ml-1 text-xs font-medium text-muted-foreground">{s.sub}</span>}</p>
          </div>
        );
      })}
    </div>
  );
}
